import { useLegalSettings } from '@/hooks/useLegalSettings';
import { Skeleton } from '@/components/ui/skeleton';
import PageHero from '@/components/layout/PageHero';

const TermsContent = () => {
  const { data: legalSettings, isLoading } = useLegalSettings();

  return (
    <div className="bg-brand-mist">
      <PageHero
        title="Terms of Service"
        description="The ground rules for using WhatsGood HK."
        contentClassName="py-12 md:py-16"
      />

      <section className="border-b border-brand-cloud bg-white px-4 py-10 md:py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="public-section-title">Using WhatsGood HK</h2>
          <p className="mt-3 leading-7 text-brand-slate">
            These terms explain the responsibilities that come with browsing, listing, reviewing, and contacting
            providers on WhatsGood HK.
          </p>
          <p className="mt-5 border-t border-brand-cloud pt-4 text-sm text-brand-slate">
            <span className="font-medium text-brand-ink">Last updated:</span>{" "}
            {isLoading ? <Skeleton className="inline-block h-4 w-32" /> : legalSettings?.last_updated_date}
          </p>
        </div>
      </section>

      <section className="px-4 py-12 md:py-16">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,0.25fr)_minmax(0,0.75fr)] lg:gap-12">
          <aside className="hidden lg:block">
            <div className="sticky top-24 border-t border-brand-cloud pt-5">
              <p className="public-card-title">Service terms</p>
              <p className="mt-2 text-sm leading-6 text-brand-slate">
                Accounts, listings, reviews, platform role, and liability.
              </p>
            </div>
          </aside>

          <article className="rounded-md border border-brand-cloud bg-white p-6 shadow-sm md:p-8">
            <div className="max-w-none text-brand-slate [&_h2:first-child]:mt-0 [&_h2]:mb-4 [&_h2]:mt-8 [&_h2]:font-heading [&_h2]:text-h3 [&_h2]:font-semibold [&_h2]:text-brand-ink [&_li]:leading-7 [&_p]:leading-7 [&_strong]:font-semibold [&_strong]:text-brand-ink">
              <div className="mb-8 rounded-md border border-harbour bg-harbour-soft p-4">
                <p className="font-medium text-brand-ink">
                  This website and all legal documents are published in English, which is the official language of service for WhatsGood HK.
                </p>
              </div>

              <h2>1. Agreement to Terms</h2>
              <p>
                By using our website, whatsgoodhk.com (the "Site"), you agree to be bound by these Terms of Service. If
                you do not agree to these terms, you may not use the Site.
              </p>

              <h2>2. User Accounts</h2>
              <p>
                You must provide accurate and complete information when creating an account. You are responsible for
                safeguarding your password and for all activities that occur under your account. You must notify us
                immediately of any unauthorized use of your account.
              </p>

              <h2>3. User-Generated Content</h2>
              <p>
                You are solely responsible for any content you post, including listings and reviews ("User Content"). You
                agree not to post User Content that is illegal, defamatory, infringing on intellectual property rights,
                or otherwise harmful. We reserve the right, but are not obligated, to remove or edit User Content that
                violates these terms.
              </p>

              <h2>4. Our Role as a Platform</h2>
              <p>
                WhatsGood HK is a neutral platform that connects users with service providers. We are not a party to any
                agreement or transaction between users and service providers. We do not hire, endorse, or guarantee the
                quality, safety, or legality of the services offered by providers listed on our Site. Any disputes are to
                be resolved directly between the user and the service provider.
              </p>

              <h2>5. Disclaimer of Warranties</h2>
              <p>
                The Site is provided on an "as is" and "as available" basis. We make no warranties, express or implied,
                regarding the operation of the Site or the information, content, or materials included on it.
              </p>

              <h2>6. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, WhatsGood HK shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly
                or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from (a) your
                access to or use of or inability to access or use the services; (b) any conduct or content of any third
                party on the services; or (c) unauthorized access, use, or alteration of your transmissions or content.
              </p>

              <h2>7. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the Hong Kong Special
                Administrative Region, without regard to its conflict of law principles.
              </p>

              <h2>8. Changes to These Terms</h2>
              <p>
                We reserve the right to modify these terms at any time. We will notify you of any changes by posting the
                new Terms of Service on this page. Your continued use of the Site after any such changes constitutes your
                acceptance of the new Terms.
              </p>

              <h2>9. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact us through the contact form available on our
                website.
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default TermsContent;
